import React, { useState, useRef, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import { setPassword as apiSetPassword } from '../utils/api';
import styles from './SignInScreen.module.css';

// Shown once, right after a fresh magic-link sign-in for a user who
// hasn't set a password yet. Collects first / last / phone +
// password (with confirm), POSTs /api/auth/set-password, and then
// the App.jsx render switch lets them through to the projects page
// because `currentUser.password_set` is now true.
//
// Phone is optional today; we collect it so MFA enrollment later
// doesn't need a separate "fill in your number" step.

export default function AccountSetupPage() {
  const currentUser    = useStore((s) => s.currentUser);
  const setCurrentUser = useStore((s) => s.setCurrentUser);

  const [firstName, setFirstName] = useState(currentUser?.first_name || '');
  const [lastName,  setLastName]  = useState(currentUser?.last_name  || '');
  const [phone,     setPhone]     = useState(currentUser?.phone      || '');
  const [pw,        setPw]        = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [error,     setError]     = useState('');
  const [saving,    setSaving]    = useState(false);
  const firstInput = useRef(null);

  useEffect(() => { firstInput.current?.focus(); }, []);

  async function submit(e) {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('First and last name are required.');
      return;
    }
    if (!phone.trim()) {
      setError('Phone number is required (used for future MFA).');
      return;
    }
    if (pw.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (pw !== pwConfirm) {
      setError('Passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      const me = await apiSetPassword({
        first_name: firstName.trim(),
        last_name:  lastName.trim(),
        phone:      phone.trim(),
        password:   pw,
      });
      setCurrentUser(me);
      toast.success('Account set up — welcome.');
      // App.jsx's render switch keys off currentUser.password_set; once
      // that flips true, the projects view renders next.
    } catch (err) {
      setError(err.message || 'Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.screen}>
      <form className={styles.card} onSubmit={submit}>
        <div className={styles.logo}>GUMBOGRID</div>
        <div className={styles.subtitle}>FINISH SETTING UP YOUR ACCOUNT</div>

        <label className={styles.label} htmlFor="setup-first">FIRST NAME</label>
        <input
          id="setup-first"
          ref={firstInput}
          className={styles.input}
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          autoComplete="given-name"
          required
        />

        <label className={styles.label} htmlFor="setup-last">LAST NAME</label>
        <input
          id="setup-last"
          className={styles.input}
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          autoComplete="family-name"
          required
        />

        <label className={styles.label} htmlFor="setup-email">EMAIL</label>
        <input
          id="setup-email"
          className={styles.input}
          value={currentUser?.email || ''}
          disabled
          style={{ opacity: 0.6, cursor: 'not-allowed' }}
        />

        <label className={styles.label} htmlFor="setup-phone">PHONE (FOR MFA)</label>
        <input
          id="setup-phone"
          type="tel"
          className={styles.input}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          autoComplete="tel"
          placeholder="(615) 555-0100"
          required
        />

        <label className={styles.label} htmlFor="setup-pw">CREATE PASSWORD</label>
        <input
          id="setup-pw"
          type="password"
          className={styles.input}
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />

        <label className={styles.label} htmlFor="setup-pw2">CONFIRM PASSWORD</label>
        <input
          id="setup-pw2"
          type="password"
          className={styles.input}
          value={pwConfirm}
          onChange={(e) => setPwConfirm(e.target.value)}
          autoComplete="new-password"
          minLength={8}
          required
        />

        {error && <div className={styles.error}>{error}</div>}

        <button
          type="submit"
          className={styles.submit}
          disabled={saving || !firstName.trim() || !lastName.trim() || !phone.trim() || !pw || !pwConfirm}
        >
          {saving ? 'SAVING…' : 'CREATE ACCOUNT →'}
        </button>

        <div className={styles.footnote}>
          You'll use your email + password to sign in next time.
          If you ever need to reset your password, ask an admin to resend
          your invitation.
        </div>
      </form>
    </div>
  );
}
